import type { ReactNode } from "react";
import type { ClubView, YouthAction, YouthRow } from "../game/types";
import { Panel, PositionChip, StrengthBadge } from "../ui/atoms";
import {
  CabecalhoDaTabela,
  CelulasDaLinha,
  ControlesDaTabela,
  LinhaVazia,
  useTabela,
  type Coluna,
} from "../ui/tabela";
import { rotuloDeCaracteristica, useT, type Chave } from "../i18n";

/**
 * Categorias de base.
 *
 * Antes desta tela a base era invisível: na virada do ano apareciam jogadores
 * novos no elenco e ninguém sabia de onde tinham vindo. Aqui estão os garotos
 * em formação o ano inteiro, com a idade, a posição, quanto cada um já cresceu
 * e até onde a comissão acha que ele pode chegar.
 *
 * O teto não aparece como número, e isso é de propósito: é a mesma faixa do
 * laudo de olheiro, e o que estreita a faixa é o nível do centro de base. Ver o
 * número exato transformaria a aposta em aritmética.
 *
 * Na virada de temporada, quem chegou aos dezoito vira decisão, e é aqui que
 * ela acontece.
 */

const VERDITOS: Record<string, Chave> = {
  vaiCrescer: "base.veredito.vaiCrescer",
  quaseFeito: "base.veredito.quaseFeito",
  noTeto: "base.veredito.noTeto",
  estagnou: "base.veredito.estagnou",
};

const CONFIANCAS: Record<string, Chave> = {
  alta: "base.confianca.alta",
  média: "base.confianca.media",
  baixa: "base.confianca.baixa",
};

/**
 * As três saídas, todas com o mesmo peso visual.
 *
 * Nenhuma nasce destacada de propósito: com "Promover" em verde, a tela parecia
 * dizer que a promoção já estava escolhida, e o botão da escolha feita ficava
 * igual ao que não tinha sido escolhido. O destaque agora quer dizer uma coisa
 * só: é esta a ordem que está na fila.
 */
const ACOES: Array<{ acao: YouthAction; rotulo: Chave; cor?: string }> = [
  { acao: "promover", rotulo: "base.promover" },
  { acao: "manter", rotulo: "base.manter" },
  { acao: "dispensar", rotulo: "base.dispensar", cor: "var(--danger)" },
];

export function YouthPanel({
  view,
  busy,
  onYouthAction,
}: {
  view: ClubView;
  busy: boolean;
  onYouthAction: (prospectId: string, action: YouthAction) => void;
}): ReactNode {
  const t = useT();
  const nivel = view.management.facilities.find((estrutura) => estrutura.key === "youth");
  const decidir = view.youth.filter((garoto) => garoto.ready);
  const formando = view.youth.filter((garoto) => !garoto.ready);
  const tabela = useTabela<YouthRow>({
    colunas: useColunasDaBase(),
    procurar: (garoto) => garoto.name,
    destaqueInicial: "forca",
  });
  const garotos = tabela.preparar(view.youth);

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Panel
        title={t("base.titulo")}
        action={
          <span className="label-muted">
            {t("base.turma", { total: view.youth.length })}
          </span>
        }
      >
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {t("base.explicacao")}
        </p>
        {nivel && (
          <div className="row" style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <span className="label">{t("base.estrutura", { nivel: nivel.level })}</span>
            {/* O texto de efeito da estrutura vem do painel do clube e só existe
                em português. Aqui a frase é do dicionário, como todo o resto. */}
            <span className="muted" style={{ fontSize: 11.5 }}>
              {t("base.oQueONivelCompra")}
            </span>
          </div>
        )}
      </Panel>

      {decidir.length > 0 && (
        <Panel
          title={t("base.decisaoTitulo")}
          action={<span className="label-muted">{t("base.decisaoDica")}</span>}
        >
          <div className="grid" style={{ gap: 10 }}>
            {decidir.map((garoto) => (
              <CartaoDeDecisao
                key={garoto.id}
                garoto={garoto}
                busy={busy}
                onYouthAction={onYouthAction}
              />
            ))}
          </div>
        </Panel>
      )}

      <Panel
        title={t("base.naBase")}
        action={
          <span className="label-muted">
            {decidir.length > 0
              ? t("base.esperandoTotal", { total: decidir.length })
              : t("base.emFormacaoTotal", { total: formando.length })}
          </span>
        }
        padded={false}
      >
        {view.youth.length === 0 ? (
          <p className="muted" style={{ margin: 0, padding: 14, fontSize: 12 }}>
            {t("base.vazia")}
          </p>
        ) : (
          <div className="scroll" style={{ maxHeight: "calc(100vh - 380px)" }}>
            <ControlesDaTabela tabela={tabela} />
            <table className="fixa">
              <CabecalhoDaTabela tabela={tabela} />
              <tbody>
                {garotos.map((garoto) => (
                  <tr className="player" key={garoto.id}>
                    <CelulasDaLinha tabela={tabela} linha={garoto} />
                  </tr>
                ))}
                {garotos.length === 0 && <LinhaVazia tabela={tabela} />}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * A decisão de um garoto.
 *
 * Os três botões continuam clicáveis depois de escolhido: trocar de ideia antes
 * de a rodada rodar é o modelo do jogo inteiro, e a fila guarda só a última
 * ordem sobre o mesmo garoto.
 */
function CartaoDeDecisao({
  garoto,
  busy,
  onYouthAction,
}: {
  garoto: YouthRow;
  busy: boolean;
  onYouthAction: (prospectId: string, action: YouthAction) => void;
}): ReactNode {
  const t = useT();
  return (
    <div
      style={{
        padding: "10px 12px",
        background: "var(--surface-2)",
        borderRadius: 8,
        border: `1px solid ${garoto.pendingAction ? "var(--primary)" : "var(--border)"}`,
      }}
    >
      <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
        <PositionChip pos={garoto.pos} />
        <span style={{ fontWeight: 700, flex: 1, minWidth: 120 }}>{garoto.name}</span>
        <span className="muted" style={{ fontSize: 11.5 }}>
          {t("base.anos", { idade: garoto.age })}
        </span>
        <StrengthBadge value={garoto.strength} />
      </div>

      <div className="row" style={{ gap: 10, marginTop: 6, flexWrap: "wrap" }}>
        <span className="num" style={{ fontWeight: 800, color: "var(--primary)" }}>
          {t("base.faixa", { baixo: garoto.potentialLow, alto: garoto.potentialHigh })}
        </span>
        <span className="label-muted">{t("base.potencialEstimado")}</span>
        <span className="label-muted">
          {t(CONFIANCAS[garoto.confidence] ?? "base.confianca.baixa")}
        </span>
      </div>

      <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
        {t(VERDITOS[garoto.verdict] ?? "base.veredito.noTeto")}
      </p>
      <p className="label-muted" style={{ margin: "4px 0 0" }}>
        {t("base.prazo", { temporada: garoto.lastSeasonAllowed })}
      </p>

      <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {ACOES.map((entrada) => {
          const escolhida = garoto.pendingAction === entrada.acao;
          return (
            <button
              key={entrada.acao}
              className={escolhida ? "primary" : "ghost"}
              disabled={busy}
              onClick={() => onYouthAction(garoto.id, entrada.acao)}
              style={{
                minHeight: 30,
                padding: "3px 12px",
                fontSize: 12,
                ...(escolhida ? {} : { color: entrada.cor }),
              }}
            >
              {t(entrada.rotulo)}
            </button>
          );
        })}
        {garoto.pendingAction && (
          <span className="label" style={{ color: "var(--accent)" }}>
            {t("comum.naFila")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * As colunas da base.
 *
 * São sete, e sete não cabem num celular. Ficam sempre a posição e o nome; as
 * outras cinco entram uma por vez, pelo chip.
 *
 * A faixa de potencial não ordena por si: ela é um intervalo, e ordenar por
 * "18 a 24" não quer dizer nada sozinho. Quem quer o garoto mais promissor
 * ordena pelo topo da faixa, que é o que a coluna usa.
 */
function useColunasDaBase(): Array<Coluna<YouthRow>> {
  const t = useT();

  return [
    {
      chave: "pos",
      rotulo: t("base.colunas.pos"),
      presenca: "sempre",
      largura: 48,
      celula: (garoto) => <PositionChip pos={garoto.pos} />,
    },
    {
      chave: "garoto",
      rotulo: t("base.colunas.garoto"),
      presenca: "sempre",
      ordenar: (garoto) => garoto.name,
      direcaoInicial: "asc",
      celula: (garoto) => (
        <div style={{ minWidth: 0 }}>
          <div className="corta" style={{ fontWeight: 600 }}>
            {garoto.name}
          </div>
          <div className="muted corta" style={{ fontSize: 11 }}>
            {garoto.highlights.map((chave) => rotuloDeCaracteristica(chave)).join(", ")}
          </div>
        </div>
      ),
    },
    {
      chave: "idade",
      rotulo: t("base.colunas.idade"),
      presenca: "destaque",
      largura: 52,
      direita: true,
      ordenar: (garoto) => garoto.age,
      direcaoInicial: "asc",
      celula: (garoto) => <span className="num">{garoto.age}</span>,
    },
    {
      chave: "forca",
      rotulo: t("base.colunas.forca"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (garoto) => garoto.strength,
      celula: (garoto) => <StrengthBadge value={garoto.strength} />,
    },
    {
      chave: "evolucao",
      rotulo: t("base.colunas.evolucao"),
      presenca: "destaque",
      largura: 74,
      direita: true,
      ordenar: (garoto) => garoto.growth,
      celula: (garoto) => (
        <span className="num" style={{ color: garoto.growth > 0 ? "var(--success)" : undefined }}>
          {garoto.growth > 0
            ? t("base.evolucaoValor", { pontos: garoto.growth })
            : t("comum.vazio")}
        </span>
      ),
    },
    {
      chave: "faixa",
      rotulo: t("base.colunas.faixa"),
      presenca: "destaque",
      largura: 92,
      direita: true,
      ordenar: (garoto) => garoto.potentialHigh,
      celula: (garoto) => (
        <span className="num" style={{ whiteSpace: "nowrap" }}>
          {t("base.faixa", { baixo: garoto.potentialLow, alto: garoto.potentialHigh })}
        </span>
      ),
    },
    {
      chave: "situacao",
      rotulo: t("base.colunas.situacao"),
      presenca: "destaque",
      largura: 130,
      celula: (garoto) => <Situacao garoto={garoto} />,
    },
  ];
}

function Situacao({ garoto }: { garoto: YouthRow }): ReactNode {
  const t = useT();
  if (garoto.pendingAction) {
    const rotulo =
      garoto.pendingAction === "promover"
        ? "base.naFilaPromover"
        : garoto.pendingAction === "manter"
          ? "base.naFilaManter"
          : "base.naFilaDispensar";
    return (
      <span className="label" style={{ color: "var(--accent)" }}>
        {t(rotulo)}
      </span>
    );
  }
  if (garoto.ready) {
    return (
      <span className="label" style={{ color: "var(--warning)" }}>
        {t("base.esperaDecisao")}
      </span>
    );
  }
  if (garoto.stagnant) {
    return (
      <span className="label" style={{ color: "var(--danger)" }}>
        {t("base.parado")}
      </span>
    );
  }
  return <span className="label-muted">{t("base.emFormacaoRotulo")}</span>;
}

/**
 * A base dentro da tela de fim de temporada.
 *
 * É a mesma decisão da tela da base, no mesmo formato, porque é a mesma
 * decisão. O balanço cobre o hub inteiro enquanto está aberto, então sem isto o
 * treinador leria "a base pede decisão" na notícia e não teria onde decidir.
 */
export function DecisoesDaBase({
  youth,
  busy,
  onYouthAction,
}: {
  youth: YouthRow[];
  busy: boolean;
  onYouthAction: (prospectId: string, action: YouthAction) => void;
}): ReactNode {
  const t = useT();
  const decidir = youth.filter((garoto) => garoto.ready);
  if (decidir.length === 0) return null;

  return (
    <Panel
      title={t("base.decisaoTitulo")}
      action={<span className="label-muted">{t("base.decisaoDica")}</span>}
      style={{ marginTop: 14 }}
    >
      <div className="grid" style={{ gap: 10 }}>
        {decidir.map((garoto) => (
          <CartaoDeDecisao
            key={garoto.id}
            garoto={garoto}
            busy={busy}
            onYouthAction={onYouthAction}
          />
        ))}
      </div>
    </Panel>
  );
}
